/**
 * Sugestão de Recompra — quanto comprar de cada SKU.
 *
 * Porte do painel publicado em Artifact (gerado por `gen_painel_recompra.py` do
 * projeto fiesta_party_dados, versão de 15/09/2026). As regras vivem em
 * `recompra/logic.ts`; aqui só se monta a tela.
 *
 * O dado vem do serviço `services/recompra` (porta 8102), via `/api/recompra/*`.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { usePanelInsightsFoco, useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { pedirAnaliseProposta } from '../api/bridge'
import {
  FILTROS_VAZIOS,
  FORNECEDOR_CONFIRMADO,
  ORDEM_PADRAO,
  ROTULO_URGENCIA,
  SEM_FORNECEDOR,
  SITUACAO_OC,
  agruparPedido,
  agruparPorFornecedor,
  ativos as somenteAtivos,
  cadastroIncompleto,
  descontinuado as estaDescontinuado,
  filtrar,
  fmtBrl,
  fmtDataHora,
  fmtDec1,
  fmtDia,
  fmtInt,
  fornecedorDe,
  fornecedorDescontinuado,
  fornecedorEhConfirmado,
  idProduto,
  linhasExport,
  linhasExportTiny,
  listaFornecedores,
  mapaDescontinuados,
  nomeArquivo,
  ordenar,
  paraCsv,
  paraTsv,
  produtoDescontinuado,
  proximaOrdem,
  somaUnidades,
  somaValor,
  totaisDaLista,
  valorEstoque as calcValorEstoque,
  type ChaveOrdem,
  type Filtros,
  type MapaDescontinuados,
  type Ordem,
} from './recompra/logic'
import {
  desmarcarDescontinuado,
  dispararAtualizacao,
  esvaziarImportacao,
  esvaziarPedido,
  fetchImportacao,
  fetchRecompra,
  fetchRecompraStatus,
  marcarDescontinuado,
  removerItemPedido,
  removerQtdImportacao,
  salvarItemPedido,
  salvarParamImportacao,
  salvarQtdImportacao,
} from './recompra/recompraService'
import {
  FILTROS_IMP_VAZIOS,
  ORDEM_IMP_PADRAO,
  PARAM_PADRAO,
  ROTULO_BASE,
  ROTULO_SITUACAO,
  demanda as demandaImp,
  diasCobertura,
  editada as qtdEditada,
  filtrarImp,
  linhasExportImportacao,
  mapaQtd,
  nomeArquivoImportacao,
  ordenarImp,
  proximaOrdemImp,
  quantidade as qtdImp,
  ritmoDe,
  ritmoDeLancamento,
  situacao as situacaoImp,
  sugerido as sugeridoImp,
  totalPedir,
  type ChaveOrdemImp,
  type FiltrosImp,
  type OrdemImp,
} from './recompra/importacao'
import type {
  BaseRitmo,
  IdadeEstoqueTiny,
  ItemPedido,
  LinhaImportacao,
  LinhaRecompra,
  ModoAtualizacao,
  ParamImportacao,
  PayloadImportacao,
  PayloadRecompra,
  StatusRecompra,
} from './recompra/tipos'

const DIAS = 180
/**
 * A lista tem milhares de SKUs e cada linha é cara (chip, botão, checkbox e vários
 * rótulos). Renderizar 200 de uma vez já deixa a aba sem resposta por dezenas de
 * segundos — medido em 18/09/2026. 50 abre na hora; o botão "Mostrar mais" busca as
 * próximas, e a contagem total fica sempre visível para ninguém achar que a lista acabou.
 */
const PAGINA = 50

type Aba = 'lista' | 'forn' | 'pedido' | 'imp' | 'valor' | 'desc' | 'parado' | 'fora' | 'metodo'

const COR_SITUACAO_IMP: Record<string, 'error' | 'warning' | 'success'> = {
  esgotado: 'error',
  baixo: 'warning',
  estoque: 'success',
}

const COR_URGENCIA: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  comprar_agora: 'error',
  atencao: 'warning',
  ok: 'success',
  sem_giro: 'default',
}

function baixar(nome: string, texto: string, mime: string) {
  const url = URL.createObjectURL(new Blob([texto], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function RecompraPage() {
  const [payload, setPayload] = useState<PayloadRecompra | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aba, setAba] = useState<Aba>('lista')
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VAZIOS)
  const [ordem, setOrdem] = useState<Ordem>(ORDEM_PADRAO)
  const [limite, setLimite] = useState(PAGINA)
  const [msg, setMsg] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  // simulação de importação: payload próprio (arquivo separado no serviço). Se o
  // serviço ainda não a calculou, a aba mostra o motivo e o resto da tela segue.
  const [payloadImp, setPayloadImp] = useState<PayloadImportacao | null>(null)
  const [erroImp, setErroImp] = useState<string | null>(null)
  const [paramImp, setParamImp] = useState<ParamImportacao>(PARAM_PADRAO)
  const [filtrosImp, setFiltrosImp] = useState<FiltrosImp>(FILTROS_IMP_VAZIOS)
  const [ordemImp, setOrdemImp] = useState<OrdemImp>(ORDEM_IMP_PADRAO)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })

  const carregarImportacao = useCallback(async () => {
    try {
      const p = await fetchImportacao(DIAS)
      setPayloadImp(p)
      setErroImp(null)
      if (p.param) setParamImp({ meses: p.param.meses, lead: p.param.lead, base: p.param.base })
    } catch (e) {
      setErroImp(e instanceof Error ? e.message : String(e))
    }
  }, [])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setPayload(await fetchRecompra(DIAS))
      await carregarImportacao()
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setCarregando(false)
    }
  }, [carregarImportacao])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // ------------------------------------------------ atualização: andamento ao vivo
  // O POST /atualizar volta na hora e o trabalho (Tiny + cálculo) segue no serviço.
  // Enquanto `atualizando`, consulta-se o /status a cada 4 s; quando termina, a base
  // é recarregada sozinha — ninguém precisa dar F5 (28/09/2026).
  const [statusVivo, setStatusVivo] = useState<StatusRecompra | null>(null)
  const [acompanhando, setAcompanhando] = useState(false)
  const atualizandoRef = useRef(false)

  useEffect(() => {
    if (payload?.status?.atualizando && !acompanhando) setAcompanhando(true)
  }, [payload?.status?.atualizando, acompanhando])

  useEffect(() => {
    if (!acompanhando) return
    let vivo = true
    const tick = async () => {
      try {
        const st = await fetchRecompraStatus()
        if (!vivo) return
        setStatusVivo(st)
        if (st.atualizando) {
          atualizandoRef.current = true
          return
        }
        // terminou (ou nunca esteve rodando): recarrega a base e para de perguntar
        setAcompanhando(false)
        if (atualizandoRef.current) {
          atualizandoRef.current = false
          await carregar()
          setMsg(
            st.ultimo_erro
              ? `A atualização falhou: ${st.ultimo_erro}`
              : st.ultimo_erro_tiny
                ? `Base recalculada, mas o Tiny não respondeu por completo: ${st.ultimo_erro_tiny}`
                : 'Base atualizada com o estoque de agora do Tiny.',
          )
        }
      } catch {
        // rede piscou: tenta no próximo tick
      }
    }
    void tick()
    const id = window.setInterval(() => void tick(), 4000)
    return () => {
      vivo = false
      window.clearInterval(id)
    }
  }, [acompanhando, carregar])

  useEffect(() => {
    const t = timers.current
    return () => {
      for (const id of Object.keys(t)) clearTimeout(t[id])
    }
  }, [])

  const desc: MapaDescontinuados = useMemo(
    () => mapaDescontinuados(payload?.descontinuados),
    [payload],
  )
  const linhas = payload?.linhas || []
  const pedido = payload?.pedido || []
  const vivos = useMemo(() => somenteAtivos(linhas, desc), [linhas, desc])
  const ve = useMemo(
    () => calcValorEstoque(linhas, payload?.estoque_parado || [], desc),
    [linhas, payload, desc],
  )
  const porSku = useMemo(() => new Map(linhas.map((l) => [l.codigo, l])), [linhas])
  const noPedido = useMemo(() => new Set(pedido.map((p) => p.sku)), [pedido])
  const fornecedores = useMemo(
    () => payload?.fornecedores || listaFornecedores(linhas),
    [payload, linhas],
  )

  // ------------------------------------------------------------------ ações de escrita
  // Todas passam por aqui: escreve, recarrega o payload (as marcações vivem no banco,
  // não no estado da tela) e mostra o motivo se falhar.
  const comRecarga = useCallback(async (fn: () => Promise<unknown>, textoErro: string) => {
    setOcupado(true)
    try {
      await fn()
      setPayload(await fetchRecompra(DIAS))
    } catch (e) {
      setMsg(`${textoErro}: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setOcupado(false)
    }
  }, [])

  // useCallback aqui não é enfeite: estes três vão como prop para cada linha da tabela.
  // Recriados a cada render, anulariam o memo de `LinhaSku` e remontariam a lista inteira.
  const descontinuar = useCallback(
    (tipo: 'produto' | 'fornecedor', valor: string, nome?: string) =>
      void comRecarga(
        () => marcarDescontinuado(tipo, valor, nome),
        'Não deu para marcar como descontinuado',
      ),
    [comRecarga],
  )

  const reativar = useCallback(
    (id: string) => void comRecarga(() => desmarcarDescontinuado(id), 'Não deu para reativar'),
    [comRecarga],
  )

  const togglePedido = useCallback(
    (l: LinhaRecompra) =>
      void comRecarga(
        () =>
          noPedido.has(l.codigo)
            ? removerItemPedido(idProduto(l.codigo))
            : salvarItemPedido({
                sku: l.codigo,
                nome: l.nome,
                forn: fornecedorDe(l, desc),
                qtd: l.quantidade_sugerida,
                sug: l.quantidade_sugerida,
              }),
        'Não deu para mexer no pedido',
      ),
    [comRecarga, noPedido, desc],
  )

  const ticarFornecedor = (itens: LinhaRecompra[]) =>
    comRecarga(async () => {
      for (const l of itens) {
        if (noPedido.has(l.codigo)) continue
        await salvarItemPedido({
          sku: l.codigo,
          nome: l.nome,
          forn: fornecedorDe(l, desc),
          qtd: l.quantidade_sugerida,
          sug: l.quantidade_sugerida,
        })
      }
    }, 'Não deu para ticar o fornecedor inteiro')

  /** Quantidade: grava com folga, para não mandar uma chamada por tecla. */
  const mudarQtd = (it: ItemPedido, valor: number) => {
    const qtd = Math.max(0, Math.round(valor || 0))
    setPayload((p) =>
      p
        ? { ...p, pedido: (p.pedido || []).map((x) => (x.id === it.id ? { ...x, qtd } : x)) }
        : p,
    )
    clearTimeout(timers.current[it.id])
    timers.current[it.id] = setTimeout(() => {
      salvarItemPedido({ sku: it.sku, nome: it.nome, forn: it.forn, qtd, sug: it.sug }).catch(
        (e: unknown) =>
          setMsg(`Não deu para salvar a quantidade: ${e instanceof Error ? e.message : String(e)}`),
      )
    }, 600)
  }

  const atualizarBase = (modo: ModoAtualizacao = 'rapido') =>
    comRecarga(async () => {
      await dispararAtualizacao(DIAS, modo)
      atualizandoRef.current = true
      setStatusVivo((s) => ({ ...(s || { ultima_atualizacao: null, ultimo_erro: null }), atualizando: true, etapa: 'tiny', tiny: null }))
      setAcompanhando(true)
      setMsg(null)
    }, 'Não deu para disparar a atualização')

  // ---------------------------------------------------------- simulação: escritas
  const qtdsImp = useMemo(() => mapaQtd(payloadImp?.quantidades), [payloadImp])

  /** Parâmetros: aplica na hora (a tela recalcula) e grava em seguida. */
  const mudarParam = (novo: Partial<ParamImportacao>) => {
    const p = { ...paramImp, ...novo }
    setParamImp(p)
    salvarParamImportacao(p).catch((e: unknown) =>
      setMsg(`Não deu para salvar os parâmetros: ${e instanceof Error ? e.message : String(e)}`),
    )
  }

  /** Quantidade por SKU: grava com folga; igual à sugestão = apaga a edição. */
  const mudarQtdImp = (l: LinhaImportacao, valor: number) => {
    if (!payloadImp) return
    const qtd = Math.max(0, Math.round(valor || 0))
    const id = idProduto(l.codigo)
    const sug = sugeridoImp(l, paramImp, payloadImp)
    setPayloadImp((p) => {
      if (!p) return p
      const resto = (p.quantidades || []).filter((q) => q.id !== id)
      return {
        ...p,
        quantidades: qtd === sug ? resto : [...resto, { id, sku: l.codigo, qtd, em: null }],
      }
    })
    clearTimeout(timers.current[`imp:${id}`])
    timers.current[`imp:${id}`] = setTimeout(() => {
      ;(qtd === sug ? removerQtdImportacao(id) : salvarQtdImportacao(l.codigo, qtd)).catch(
        (e: unknown) =>
          setMsg(`Não deu para salvar a quantidade: ${e instanceof Error ? e.message : String(e)}`),
      )
    }, 600)
  }

  const exportarImp = (arr: LinhaImportacao[], formato: 'csv' | 'copiar') => {
    if (!payloadImp || !arr.length) return
    const aoa = linhasExportImportacao(arr, paramImp, payloadImp, qtdsImp)
    if (formato === 'copiar') {
      navigator.clipboard
        .writeText(paraTsv(aoa))
        .then(() => setMsg(`${arr.length} linhas copiadas — cole direto numa planilha.`))
        .catch(() => setMsg('Não foi possível copiar neste navegador.'))
      return
    }
    baixar(nomeArquivoImportacao(paramImp), paraCsv(aoa), 'text/csv;charset=utf-8')
    setMsg(`Arquivo gerado com ${arr.length} SKUs.`)
  }

  // ------------------------------------------------------------------------ exportação
  const exportar = (
    itens: ItemPedido[],
    forn: string | null,
    formato: 'csv' | 'copiar' | 'oc-tiny',
  ) => {
    if (!itens.length) return
    if (formato === 'oc-tiny') {
      // layout de importação de Pedido de Compra do Tiny, 13 colunas
      baixar(
        nomeArquivo(forn, 'csv', new Date(), 'oc_tiny'),
        paraCsv(linhasExportTiny(itens, porSku)),
        'text/csv;charset=utf-8',
      )
      setMsg(
        `Planilha de OC gerada com ${itens.length} SKUs. Preencha o Valor unitário antes ` +
          'de importar no Tiny — o painel não sabe o preço negociado.',
      )
      return
    }
    const aoa = linhasExport(itens, porSku)
    if (formato === 'copiar') {
      navigator.clipboard
        .writeText(paraTsv(aoa))
        .then(() => setMsg(`${itens.length} linhas copiadas — cole direto numa planilha.`))
        .catch(() => setMsg('Não foi possível copiar neste navegador.'))
      return
    }
    baixar(nomeArquivo(forn, 'csv'), paraCsv(aoa), 'text/csv;charset=utf-8')
    setMsg(`Arquivo gerado com ${itens.length} SKUs.`)
  }

  // ------------------------------------------------------------------------------ KPIs
  const comprar = vivos.filter((l) => l.urgencia === 'comprar_agora').length
  const atencao = vivos.filter((l) => l.urgencia === 'atencao').length
  const confirmados = vivos.filter((l) => fornecedorEhConfirmado(l, desc)).length
  const nDesc = linhas.length - vivos.length

  const stats = payload
    ? [
        { label: 'Comprar agora', value: fmtInt(comprar), hint: 'cobertura ≤ lead time' },
        { label: 'Atenção', value: fmtInt(atencao), hint: 'abaixo do ponto de reposição' },
        {
          label: 'Valor em estoque',
          value: fmtBrl(ve.total),
          hint: `só o que tem giro · ${fmtInt(somaUnidades(ve.parados))} un sem giro não valoradas`,
        },
        {
          label: 'SKUs ativos',
          value: fmtInt(vivos.length),
          hint: `de ${fmtInt(linhas.length)} com giro em ${payload.janela.dias} dias`,
        },
        { label: 'Descontinuados', value: fmtInt(nDesc), hint: 'fora da sugestão' },
        {
          label: 'Fornecedor confirmado',
          value: fmtInt(confirmados),
          hint: 'sem cadastro no Tiny → Bellocopo',
        },
      ]
    : undefined

  // ---- estado da atualização, para o cabeçalho ----
  const emAndamento = Boolean(statusVivo?.atualizando ?? payload?.status?.atualizando)
  const textoAndamento = (() => {
    const st = statusVivo
    if (!st) return 'Disparando…'
    if (st.etapa === 'tiny') {
      const t = st.tiny
      return t?.total
        ? `Consultando o Tiny: ${fmtInt(t.feitos)} de ${fmtInt(t.total)} SKUs${
            t.erros ? ` · ${fmtInt(t.erros)} sem resposta` : ''
          }`
        : 'Consultando o Tiny…'
    }
    if (st.etapa === 'calculo') return 'Recalculando a sugestão (≈2 min)…'
    if (st.etapa === 'simulacao') return 'Montando a simulação de importação…'
    return 'Atualizando…'
  })()
  const idadeEstoque: IdadeEstoqueTiny | undefined = (() => {
    const v = statusVivo?.estoque_tiny_em
    if (v && !('erro' in v)) return v
    return payload?.estoque_tiny?.idade
  })()
  const rotuloEstoque = (() => {
    if (!idadeEstoque) return 'Estoque do Tiny: data desconhecida'
    const partes = Object.entries(idadeEstoque)
      .filter(([, v]) => v.em)
      .map(([k, v]) => `${k === 'fiesta_party' ? 'matriz' : k} ${fmtDataHora(v.em)}`)
    return partes.length ? `Estoque do Tiny: ${partes.join(' · ')}` : 'Estoque do Tiny: sem data'
  })()
  const idadeDias = (() => {
    if (!idadeEstoque) return null
    const datas = Object.values(idadeEstoque)
      .map((v) => (v.em ? new Date(v.em).getTime() : NaN))
      .filter((n) => !Number.isNaN(n))
    if (!datas.length) return null
    return Math.floor((Date.now() - Math.min(...datas)) / 86_400_000)
  })()

  const subtitulo = payload
    ? `Quanto comprar de cada SKU, pelo giro real das notas fiscais dos últimos ${
        payload.janela.dias
      } dias (${fmtDia(payload.janela.de)} a ${fmtDia(payload.janela.ate)}) e pelo estoque do ` +
      `Tiny. Somente SKU simples. Base calculada em ${fmtDataHora(
        payload.status?.ultima_atualizacao || payload.gerado_em,
      )}.`
    : 'Quanto comprar de cada SKU, pelo giro real das notas fiscais e pelo estoque do Tiny.'

  const { focoRef } = usePanelInsightsFoco()
  const carregarLeitura = useCallback(async () => {
    if (!payload || carregando || erro) return
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const fallback = [
      `Comprar agora: ${fmtInt(comprar)} SKUs · Atenção: ${fmtInt(atencao)}.`,
      `Valor em estoque (só com giro): ${fmtBrl(ve.total)}.`,
      `SKUs ativos: ${fmtInt(vivos.length)} de ${fmtInt(linhas.length)} com giro em ${payload.janela.dias} dias.`,
      `Descontinuados: ${fmtInt(nDesc)} · fornecedor confirmado (Bellocopo): ${fmtInt(confirmados)}.`,
      'Em trânsito não descontado (API Tiny sem distinção). Pedido = quantidade, sem valor a gastar.',
      'Verdade = KPIs e tabelas desta tela (serviço /api/recompra).',
    ].join('\n')
    const data = await pedirAnaliseProposta({
      tipo: 'insight_reflexao',
      insight: {
        dominio: 'recompra',
        dominio_label: 'Recompra',
        titulo: 'Leitura da sugestão de recompra',
        leitura: fallback,
        ...(focoRef.current ? { foco_usuario: focoRef.current } : {}),
        periodo: `${payload.janela.de} a ${payload.janela.ate}`,
        metricas: {
          comprar_agora: comprar,
          atencao,
          valor_estoque: ve.total,
          skus_ativos: vivos.length,
          skus_com_giro: linhas.length,
          descontinuados: nDesc,
          fornecedor_confirmado: confirmados,
          dias: payload.janela.dias,
        },
      },
    })
    if (!data.ok) {
      setLeituraIa({ loading: false, texto: fallback, fonte: 'regras', modelo: null, erro: null })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.leitura || data.texto || fallback,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
    })
  }, [
    payload,
    carregando,
    erro,
    comprar,
    atencao,
    ve.total,
    vivos.length,
    linhas.length,
    nDesc,
    confirmados,
  ])

  useEffect(() => {
    if (payload && !carregando && !erro) void carregarLeitura()
  }, [payload?.gerado_em, carregando, erro]) // eslint-disable-line react-hooks/exhaustive-deps

  useRegisterPanelInsights({
    titulo: 'Recompra',
    verdade: 'Verdade = KPIs e tabelas desta tela (serviço /api/recompra). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: carregarLeitura,
  })

  // ------------------------------------------------------------------------ sub-blocos
  const cabecalho = (comForn: boolean) => {
    const col = (k: ChaveOrdem, rotulo: string, alinha: 'left' | 'right' = 'right') => (
      <TableCell key={k} align={alinha} sortDirection={ordem.k === k ? (ordem.dir === 1 ? 'asc' : 'desc') : false}>
        <TableSortLabel
          active={ordem.k === k}
          direction={ordem.k === k && ordem.dir === -1 ? 'desc' : 'asc'}
          onClick={() => setOrdem((o) => proximaOrdem(o, k))}
        >
          {rotulo}
        </TableSortLabel>
      </TableCell>
    )
    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" title="incluir no pedido">
            ✓
          </TableCell>
          {col('nome', 'Produto', 'left')}
          {comForn ? col('fornecedor', 'Fornecedor', 'left') : null}
          {col('urgencia', 'Situação')}
          {col('estoque', 'Estoque')}
          {col('valor_estoque', 'Em estoque R$')}
          {col('giro_diario', 'Giro/dia')}
          {col('cobertura_dias', 'Cobertura')}
          {col('ponto_reposicao', 'Ponto rep.')}
          {col('quantidade_sugerida', 'Comprar')}
          {col('unidades_periodo', 'Vendidas')}
          {col('lead_time_dias', 'Lead')}
          <TableCell />
        </TableRow>
      </TableHead>
    )
  }

  const linhaTabela = (l: LinhaRecompra, comForn: boolean) => (
    <LinhaSku
      key={l.codigo}
      l={l}
      comForn={comForn}
      morto={estaDescontinuado(l, desc)}
      porFornecedor={
        !produtoDescontinuado(l, desc) && fornecedorDescontinuado(l.fornecedor, desc)
      }
      soProduto={produtoDescontinuado(l, desc)}
      dentro={noPedido.has(l.codigo)}
      forn={fornecedorDe(l, desc)}
      confirmado={fornecedorEhConfirmado(l, desc)}
      ocupado={ocupado}
      onToggle={togglePedido}
      onDescontinuar={descontinuar}
      onReativar={reativar}
    />
  )


  // ---------------------------------------------------------------------- aba: a lista
  const filtradas = useMemo(
    () => ordenar(filtrar(linhas, filtros, desc), ordem, desc),
    [linhas, filtros, desc, ordem],
  )
  const totais = useMemo(() => totaisDaLista(filtradas), [filtradas])

  const abaLista = () => (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          size="small"
          placeholder="Buscar SKU ou produto…"
          value={filtros.q}
          onChange={(e) => {
            setFiltros((f) => ({ ...f, q: e.target.value }))
            setLimite(PAGINA)
          }}
          sx={{ minWidth: 230, flex: 1 }}
        />
        {/* displayEmpty: sem isto o MUI não mostra o rótulo da opção de valor "" */}
        <TextField
          select
          size="small"
          label="Fornecedor"
          value={filtros.f}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          onChange={(e) => {
            setFiltros((f) => ({ ...f, f: e.target.value }))
            setLimite(PAGINA)
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">Todos os fornecedores</MenuItem>
          <MenuItem value="__sem__">{SEM_FORNECEDOR}</MenuItem>
          {fornecedores.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Situação"
          value={filtros.u}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          onChange={(e) => {
            setFiltros((f) => ({ ...f, u: e.target.value }))
            setLimite(PAGINA)
          }}
          sx={{ minWidth: 210 }}
        >
          <MenuItem value="">Todas as situações</MenuItem>
          <MenuItem value="comprar_agora">Comprar agora</MenuItem>
          <MenuItem value="atencao">Atenção</MenuItem>
          <MenuItem value="ok">OK</MenuItem>
          <MenuItem value="__inc__">⚠ Cadastro incompleto</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Origem"
          value={filtros.cl}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          onChange={(e) => {
            setFiltros((f) => ({ ...f, cl: e.target.value }))
            setLimite(PAGINA)
          }}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">Nacional e importado</MenuItem>
          <MenuItem value="importado">Só importado</MenuItem>
          <MenuItem value="nacional">Só nacional</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {fmtInt(filtradas.length)} de {fmtInt(vivos.length)} ativos
        </Typography>
      </Stack>

      {/* o total acompanha o filtro: escolher um fornecedor mostra o estoque dele */}
      <Paper variant="outlined" sx={{ p: 1.2 }}>
        <Typography variant="body2">
          <b>{fmtBrl(totais.valor)}</b> em estoque · {fmtInt(totais.unidades)} unidades
          {totais.revisaoSkus > 0 ? (
            <Box component="span" sx={{ color: 'warning.main' }}>
              {' · '}
              {fmtBrl(totais.revisaoValor)} em {totais.revisaoSkus} SKU
              {totais.revisaoSkus > 1 ? 's' : ''} com CMV em revisão, fora da soma
            </Box>
          ) : null}
          {totais.semCmvSkus > 0 ? (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' · '}
              {totais.semCmvSkus} com saldo e sem CMV
            </Box>
          ) : null}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          {cabecalho(true)}
          <TableBody>
            {filtradas.slice(0, limite).map((l) => linhaTabela(l, true))}
            {filtradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13}>
                  <Typography variant="body2" color="text.secondary">
                    Nada encontrado com esses filtros.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
      {filtradas.length > limite ? (
        <Button onClick={() => setLimite((n) => n + PAGINA)}>
          Mostrar mais {Math.min(PAGINA, filtradas.length - limite)} (de{' '}
          {fmtInt(filtradas.length - limite)} restantes)
        </Button>
      ) : null}
    </Stack>
  )

  // -------------------------------------------------------------- aba: por fornecedor
  const grupos = useMemo(() => agruparPorFornecedor(linhas, desc), [linhas, desc])

  const abaFornecedor = () => (
    <Stack spacing={1.5}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2">
          Só os SKUs que precisam de compra (<b>Comprar agora</b> e <b>Atenção</b>). Cada bloco é um
          pedido em potencial. Descontinuar o fornecedor tira todos os produtos dele da sugestão de
          uma vez.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Tique na coluna <b>✓</b> para jogar o SKU na aba <b>Pedido</b>, ou use{' '}
          <b>✓ Ticar todos</b> para levar o fornecedor inteiro.
        </Typography>
      </Alert>
      {grupos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum fornecedor com item a comprar.
        </Typography>
      ) : null}
      {grupos.map((g) => (
        <Accordion key={g.nome} defaultExpanded={g.urgentes > 0}>
          <AccordionSummary>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ width: '100%' }}
            >
              <Typography sx={{ fontWeight: 600 }}>{g.nome}</Typography>
              <Typography variant="caption" color="text.secondary">
                {g.itens.length} SKUs · {g.urgentes} urgente{g.urgentes !== 1 ? 's' : ''} ·{' '}
                {fmtBrl(g.valorEstoque)} em estoque
              </Typography>
              <Button
                size="small"
                disabled={ocupado}
                onClick={(e) => {
                  e.stopPropagation()
                  void ticarFornecedor(g.itens)
                }}
              >
                ✓ Ticar todos
              </Button>
              {g.real ? (
                <Button
                  size="small"
                  color="inherit"
                  disabled={ocupado}
                  onClick={(e) => {
                    e.stopPropagation()
                    void descontinuar('fornecedor', g.nome)
                  }}
                >
                  ⊘ Descontinuar fornecedor
                </Button>
              ) : null}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ overflowX: 'auto', p: 0 }}>
            <Table size="small">
              {cabecalho(false)}
              <TableBody>{g.itens.map((l) => linhaTabela(l, false))}</TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  )

  // --------------------------------------------------------------------- aba: pedido
  const gruposPedido = useMemo(() => agruparPedido(pedido), [pedido])
  const unidadesPedido = pedido.reduce((s, i) => s + (Number(i.qtd) || 0), 0)

  const botoesExport = (itens: ItemPedido[], forn: string | null, prefixo: string) => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ my: 1 }}>
      <Tooltip title="13 colunas no layout que o Tiny importa. O Valor unitário sai em branco: preencha com o preço negociado antes de subir.">
        <Button variant="contained" size="small" onClick={() => exportar(itens, forn, 'oc-tiny')}>
          ⤓ Planilha de OC do Tiny
        </Button>
      </Tooltip>
      <Button size="small" onClick={() => exportar(itens, forn, 'csv')}>
        ⤓ CSV de conferência
      </Button>
      <Button size="small" onClick={() => exportar(itens, forn, 'copiar')}>
        ⧉ Copiar para planilha
      </Button>
      <Typography variant="caption" color="text.secondary">
        {prefixo} {itens.length} SKU{itens.length !== 1 ? 's' : ''}
      </Typography>
    </Stack>
  )

  const abaPedido = () => (
    <Stack spacing={1.5}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2">
          Tique os SKUs em <b>Todos os SKUs</b> ou em <b>Por fornecedor</b> e eles caem aqui,
          separados por fornecedor. A <b>quantidade vem da sugestão do painel e é editável</b> — o
          que você digitar é o que sai no arquivo. A seleção fica salva no banco e{' '}
          <b>quem abrir a tela vê o mesmo pedido</b>.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <b>Confira no Tiny antes de fechar.</b> O painel não desconta o que já foi pedido — a
          situação da ordem de compra vem <code>"0"</code> em todas as ordens, então não há como
          saber o que está a caminho.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <b>Planilha de OC do Tiny</b> sai nas 13 colunas do modelo de importação do próprio ERP,
          uma linha por item, com a situação <b>"{SITUACAO_OC}"</b> e a data de hoje. O{' '}
          <b>Valor unitário sai em branco de propósito</b>: o painel não sabe o preço negociado com
          o fornecedor. Preencha antes de importar. Se a importação exigir <code>.xls</code>, abra o
          arquivo no Excel e salve nesse formato.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Nada aqui é escrito no Tiny: o arquivo é para você subir ou repassar ao fornecedor. O{' '}
          <b>CSV de conferência</b> traz os números que justificam a compra (estoque, giro,
          cobertura), que a planilha de OC não tem.
        </Typography>
      </Alert>

      {pedido.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Nenhum SKU no pedido ainda.</Typography>
          <Typography variant="body2" color="text.secondary">
            Na coluna <b>✓</b> das listas, tique o que você quer comprar. Em <b>Por fornecedor</b> dá
            para pegar o fornecedor inteiro de uma vez.
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              <b>{pedido.length}</b> SKUs no pedido
            </Typography>
            <Typography variant="body2">
              <b>{fmtInt(unidadesPedido)}</b> unidades
            </Typography>
            <Typography variant="body2">
              <b>{gruposPedido.length}</b> fornecedores
            </Typography>
          </Stack>
          {botoesExport(pedido, null, 'pedido completo —')}
          <Box>
            <Button
              size="small"
              color="error"
              disabled={ocupado}
              onClick={() => void comRecarga(esvaziarPedido, 'Não deu para esvaziar')}
            >
              ✕ Esvaziar o pedido
            </Button>
          </Box>
          {gruposPedido.map((g) => (
            <Accordion key={g.nome} defaultExpanded>
              <AccordionSummary>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 600 }}>{g.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.itens.length} SKU{g.itens.length !== 1 ? 's' : ''} · {fmtInt(g.unidades)}{' '}
                    unidades
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ overflowX: 'auto' }}>
                {botoesExport(g.itens, g.nome, 'só deste fornecedor —')}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Estoque</TableCell>
                      <TableCell align="right">Giro/dia</TableCell>
                      <TableCell align="right">Cobertura</TableCell>
                      <TableCell align="right">Sugerido</TableCell>
                      <TableCell align="right">Comprar</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.itens.map((i) => {
                      const l = porSku.get(i.sku)
                      return (
                        <TableRow key={i.id}>
                          <TableCell>
                            <Typography variant="body2">{i.nome || l?.nome || i.sku}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {i.sku}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtInt(l?.estoque)}</TableCell>
                          <TableCell align="right">{fmtDec1(l?.giro_diario)}</TableCell>
                          <TableCell align="right">
                            {l?.cobertura_dias == null ? '—' : `${fmtDec1(l.cobertura_dias)}d`}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color="text.secondary">
                              {fmtInt(i.sug)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={i.qtd}
                              onChange={(e) => mudarQtd(i, Number(e.target.value))}
                              slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'right' } } }}
                              sx={{ width: 96 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              color="inherit"
                              disabled={ocupado}
                              onClick={() =>
                                void comRecarga(
                                  () => removerItemPedido(i.id),
                                  'Não deu para tirar do pedido',
                                )
                              }
                            >
                              ✕ Tirar
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell colSpan={4} />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {fmtInt(g.unidades)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Stack>
  )

  // ------------------------------------------------------- aba: simular importação
  const linhasImp = useMemo(
    () =>
      payloadImp
        ? ordenarImp(filtrarImp(payloadImp.linhas, filtrosImp), ordemImp, paramImp, payloadImp, qtdsImp)
        : [],
    [payloadImp, filtrosImp, ordemImp, paramImp, qtdsImp],
  )

  const abaImportacao = () => {
    if (!payloadImp) {
      return (
        <Alert severity="warning">
          A simulação de importação ainda não está disponível
          {erroImp ? `: ${erroImp}` : '.'} Ela é calculada junto com a base — use{' '}
          <b>↻ Atualizar agora</b>.
        </Alert>
      )
    }
    const I = payloadImp
    const dias = diasCobertura(paramImp, I)
    const nEsg = I.linhas.filter((l) => l.esgotado).length
    const nMud = I.linhas.filter((l) => qtdEditada(l, paramImp, I, qtdsImp)).length
    const total = totalPedir(linhasImp, paramImp, I, qtdsImp)
    const col = (k: ChaveOrdemImp, rotulo: string, alinha: 'left' | 'right' = 'right') => (
      <TableCell key={k} align={alinha} sortDirection={ordemImp.k === k ? (ordemImp.dir === 1 ? 'asc' : 'desc') : false}>
        <TableSortLabel
          active={ordemImp.k === k}
          direction={ordemImp.k === k && ordemImp.dir === -1 ? 'desc' : 'asc'}
          onClick={() => setOrdemImp((o) => proximaOrdemImp(o, k))}
        >
          {rotulo}
        </TableSortLabel>
      </TableCell>
    )
    const outros = (l: LinhaImportacao) =>
      paramImp.base === 'ativo'
        ? `30d ${fmtDec1(l.ritmo_30d)} · média ${fmtDec1(l.ritmo_medio)}`
        : paramImp.base === 'r30'
          ? `ativo ${fmtDec1(l.ritmo_ativo)} · média ${fmtDec1(l.ritmo_medio)}`
          : `ativo ${fmtDec1(l.ritmo_ativo)} · 30d ${fmtDec1(l.ritmo_30d)}`

    return (
      <Stack spacing={1.5}>
        <Alert severity="info" icon={false}>
          <Typography variant="body2">
            Quanto pedir de cada SKU importado (
            {I.criterio ?? <>origem estrangeira no cadastro do Tiny ou código terminado em <code>{I.sufixo}</code></>})
            para cobrir <b>{paramImp.meses} meses de venda</b>
            {paramImp.lead ? <> <b>mais o lead time</b> de {I.parametros.lead} dias</> : null} — {dias}{' '}
            dias no total. <b>Pedir = ritmo × {dias} − estoque de hoje.</b> A quantidade é editável
            e fica salva; o que você digitar é o que sai no arquivo.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>Por que não usar o giro do painel principal:</b> ele divide as vendas por todos os
            dias desde a estreia, inclusive os dias em que o item <b>já estava esgotado</b> — e aí
            a demanda sai menor do que foi. O <b>ritmo ativo</b> divide só pelos dias em que havia
            estoque. Para quem não esgotou, os dois são iguais. {nEsg} SKU{nEsg !== 1 ? 's' : ''}{' '}
            da linha esgotaram na janela.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>Estoque de hoje:</b> saldo do Tiny de <b>{fmtDataHora(I.planilha_em)}</b>{' '}
            menos as vendas em nota fiscal desde então (com <b>↻ Atualizar agora</b> o saldo
            vem da API do Tiny na hora). A última
            importação vem das ordens de compra do Tiny
            {I.oc_cache_em ? ` (cache de ${fmtDia(I.oc_cache_em)})` : ' — cache ausente neste serviço'}
            ; o que ainda estiver a caminho <b>não é descontado</b> — a situação da ordem não é
            confiável.
          </Typography>
        </Alert>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            label="Meses de estoque"
            size="small"
            type="number"
            value={paramImp.meses}
            onChange={(e) =>
              mudarParam({ meses: Math.min(24, Math.max(1, Math.round(Number(e.target.value) || 0))) })
            }
            slotProps={{ htmlInput: { min: 1, max: 24, step: 1 } }}
            sx={{ width: 150 }}
          />
          <TextField
            label="Base do ritmo"
            size="small"
            select
            value={paramImp.base}
            onChange={(e) => mudarParam({ base: e.target.value as BaseRitmo })}
            sx={{ minWidth: 300 }}
          >
            {(Object.keys(ROTULO_BASE) as BaseRitmo[]).map((b) => (
              <MenuItem key={b} value={b}>
                {ROTULO_BASE[b]}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant={paramImp.lead ? 'contained' : 'outlined'}
            onClick={() => mudarParam({ lead: !paramImp.lead })}
          >
            {paramImp.lead ? '✓ ' : ''}somar o lead time ({I.parametros.lead} dias)
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Typography variant="body2">
            <b>{linhasImp.length}</b> SKUs na simulação (de {I.linhas.length} com venda)
          </Typography>
          <Typography variant="body2" color="error.main">
            <b>{nEsg}</b> esgotaram
          </Typography>
          <Typography variant="body2">
            <b>{fmtInt(total)}</b> unidades a pedir
            {nMud ? ` · ${nMud} quantidade${nMud > 1 ? 's' : ''} editada${nMud > 1 ? 's' : ''}` : ''}
          </Typography>
          <Typography variant="body2">
            cobertura pedida <b>{dias}d</b>
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar SKU ou produto…"
            value={filtrosImp.q}
            onChange={(e) => setFiltrosImp((f) => ({ ...f, q: e.target.value }))}
            sx={{ minWidth: 240, flex: 1 }}
          />
          <TextField
            size="small"
            select
            value={filtrosImp.fam}
            onChange={(e) => setFiltrosImp((f) => ({ ...f, fam: e.target.value }))}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Todas as famílias</MenuItem>
            {I.familias.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant={filtrosImp.esg ? 'contained' : 'outlined'}
            color="error"
            onClick={() => setFiltrosImp((f) => ({ ...f, esg: !f.esg }))}
          >
            só esgotados
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button variant="contained" size="small" onClick={() => exportarImp(linhasImp, 'csv')}>
            ⤓ CSV da simulação
          </Button>
          <Button size="small" onClick={() => exportarImp(linhasImp, 'copiar')}>
            ⧉ Copiar para planilha
          </Button>
          <Button
            size="small"
            color="error"
            disabled={ocupado || !nMud}
            onClick={() =>
              void comRecarga(async () => {
                await esvaziarImportacao()
                await carregarImportacao()
              }, 'Não deu para voltar à sugestão')
            }
          >
            ✕ Voltar tudo à sugestão
          </Button>
          <Typography variant="caption" color="text.secondary">
            parâmetros e quantidades ficam salvos e compartilhados
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {col('nome', 'Produto', 'left')}
                <TableCell>Última importação</TableCell>
                {col('unidades', 'Vendido')}
                <TableCell>Semanas desde a estreia</TableCell>
                <TableCell>Situação</TableCell>
                {col('estoque_hoje_estimado', 'Estoque hoje')}
                {col('ritmo', 'Ritmo un/dia')}
                {col('cobertura_hoje', 'Cobertura')}
                {col('demanda', `Demanda ${dias}d`)}
                {col('pedir', 'Pedir')}
              </TableRow>
            </TableHead>
            <TableBody>
              {linhasImp.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      Nada encontrado com esses filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {linhasImp.map((l) => {
                const sit = situacaoImp(l)
                const q = qtdImp(l, paramImp, I, qtdsImp)
                const sug = sugeridoImp(l, paramImp, I)
                const mudou = q !== sug
                return (
                  <TableRow
                    key={l.codigo}
                    sx={{ borderLeft: 3, borderLeftColor: `${COR_SITUACAO_IMP[sit]}.main` }}
                  >
                    <TableCell>
                      <Typography variant="body2">{l.nome}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {l.codigo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {l.oc_ref ? (
                        <>
                          <Typography variant="caption" display="block">
                            OC {String(l.oc_ref.numero ?? '')} · {fmtDia(l.oc_ref.data)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {fmtInt(l.oc_ref.qtd)} un
                            {l.vendido_desde_oc_ref != null
                              ? ` · vendidas ${fmtInt(l.vendido_desde_oc_ref)} desde então`
                              : ''}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          <i>sem ordem no cache</i>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {fmtInt(l.unidades)}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {l.ja_vendia_antes ? `desde ${fmtDia(l.estreia)} (janela)` : `estreou ${fmtDia(l.primeira_venda)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Sparkline semanas={l.semanas} esgotouNaSemana={l.semana_esgotou} esgotouEm={l.esgotou_em} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={COR_SITUACAO_IMP[sit]}
                        label={sit === 'esgotado' ? `Esgotou ${fmtDia(l.esgotou_em)}` : ROTULO_SITUACAO[sit]}
                      />
                      {l.esgotado ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {l.esgotou_por === 'acumulado alcancou a OC' ? 'vendas alcançaram a OC' : 'pela última venda'}
                          {l.vendas_apos_esgotar ? ` · +${fmtInt(l.vendas_apos_esgotar)} un depois` : ''}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      {fmtInt(l.estoque_hoje_estimado)}
                      <Typography variant="caption" color="text.secondary" display="block">
                        planilha {fmtInt(l.estoque_planilha)} − {fmtInt(l.vendas_apos_planilha)} vendidas
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {fmtDec1(ritmoDe(l, paramImp.base))}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {outros(l)}
                      </Typography>
                      {ritmoDeLancamento(l) ? (
                        <Typography variant="caption" color="warning.main" display="block">
                          só {l.dias_ativos} dias ativos — ritmo de lançamento
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {l.dias_ativos} dias ativos
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {l.cobertura_hoje ? `${fmtDec1(l.cobertura_hoje)}d` : '—'}
                    </TableCell>
                    <TableCell align="right">{fmtInt(demandaImp(l, paramImp, I))}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={q}
                        onChange={(e) => mudarQtdImp(l, Number(e.target.value))}
                        slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'right' } } }}
                        sx={{ width: 96 }}
                        color={mudou ? 'success' : undefined}
                        focused={mudou || undefined}
                      />
                      {mudou ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          sugerido {fmtInt(sug)}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Paper>

        {I.sem_cadastro.length ? (
          <Typography variant="caption" color="text.secondary">
            {I.sem_cadastro.length} código{I.sem_cadastro.length > 1 ? 's' : ''} da nota sem
            cadastro simples no Tiny ficou fora:{' '}
            {I.sem_cadastro.map((s) => `${s.codigo_nf} (${fmtInt(s.unid)} un)`).join(', ')}.
          </Typography>
        ) : null}
      </Stack>
    )
  }

  // -------------------------------------------------------------- aba: valor do estoque
  const topValor = useMemo(
    () =>
      [...ve.ativos, ...ve.descontinuados]
        .sort((a, b) => (b.valor_estoque || 0) - (a.valor_estoque || 0))
        .slice(0, 25),
    [ve],
  )

  const abaValor = () => (
    <Stack spacing={2}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2">
          Quanto custou o que está parado no depósito hoje: <b>saldo do Tiny × CMV</b>. O custo vem
          de <code>ecommerce_financial.cmvs</code>, o registro mais recente de cada SKU — não do{' '}
          <code>preco_custo</code> do Tiny, que cobre menos SKUs e traz R$ 1,00 de marcador na linha
          importada.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <b>Só é valorado o que tem giro.</b> SKU sem nenhuma venda no período entra em unidades e{' '}
          <b>sem valor</b>: sem nota não existe preço de saída para conferir o custo contra, e custo
          não conferido não vira número aqui.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Só estoque próprio de SKU simples. Full do ML, Full Shopee e FBA não estão aqui.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Grupo</TableCell>
              <TableCell align="right">SKUs</TableCell>
              <TableCell align="right">Unidades</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>O que é</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Com giro</TableCell>
              <TableCell align="right">{ve.ativos.length}</TableCell>
              <TableCell align="right">{fmtInt(somaUnidades(ve.ativos))}</TableCell>
              <TableCell align="right">
                <b>{fmtBrl(somaValor(ve.ativos))}</b>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  vendeu nos últimos {payload?.janela.dias} dias
                </Typography>
              </TableCell>
            </TableRow>
            {ve.descontinuados.length > 0 ? (
              <TableRow>
                <TableCell>Descontinuado</TableCell>
                <TableCell align="right">{ve.descontinuados.length}</TableCell>
                <TableCell align="right">{fmtInt(somaUnidades(ve.descontinuados))}</TableCell>
                <TableCell align="right">
                  <b>{fmtBrl(somaValor(ve.descontinuados))}</b>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    marcado por você — estoque a escoar
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            <TableRow sx={{ '& td': { borderTop: 2, fontWeight: 700 } }}>
              <TableCell>Total</TableCell>
              <TableCell align="right">{ve.ativos.length + ve.descontinuados.length}</TableCell>
              <TableCell align="right">
                {fmtInt(somaUnidades(ve.ativos) + somaUnidades(ve.descontinuados))}
              </TableCell>
              <TableCell align="right">{fmtBrl(ve.total)}</TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  valor imobilizado apurável
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Sem giro</TableCell>
              <TableCell align="right">{ve.parados.length}</TableCell>
              <TableCell align="right">{fmtInt(somaUnidades(ve.parados))}</TableCell>
              <TableCell align="right">
                <Typography variant="caption" color="warning.main">
                  não valorado
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  saldo sem nenhuma venda no período — sem preço de saída para conferir o CMV
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Fora do total
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          <b>
            Custo em revisão — {ve.emRevisao.length} SKUs, {fmtBrl(somaValor(ve.emRevisao))}.
          </b>{' '}
          O CMV cadastrado passa de <b>3× o preço pelo qual o próprio SKU saiu na nota fiscal</b>.
          Não é julgamento sobre o produto: é o custo dele contra o preço de venda dele. Ficam fora
          da soma até o custo ser conferido — nenhum deles foi apagado.
        </Typography>
        {ve.emRevisao.length > 0 ? (
          <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Estoque</TableCell>
                  <TableCell align="right">CMV un.</TableCell>
                  <TableCell align="right">Preço médio na NF</TableCell>
                  <TableCell align="right">Valor que ficou fora</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...ve.emRevisao]
                  .sort((a, b) => (b.valor_estoque || 0) - (a.valor_estoque || 0))
                  .map((l) => (
                    <TableRow key={l.codigo}>
                      <TableCell>
                        <Typography variant="body2">{l.nome}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {l.codigo}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmtInt(l.estoque)}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>
                        {fmtBrl(l.cmv_unitario)}
                      </TableCell>
                      <TableCell align="right">{fmtBrl(l.preco_medio_nf)}</TableCell>
                      <TableCell align="right">{fmtBrl(l.valor_estoque)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Paper>
        ) : null}
        <Typography variant="body2" color="text.secondary" paragraph>
          <b>
            Sem CMV — {ve.semCmv.length} SKUs com giro, {fmtInt(somaUnidades(ve.semCmv))} unidades.
          </b>{' '}
          Têm saldo no Tiny e nenhum custo cadastrado em <code>cmvs</code>. Não dá para valorar:
          entram como "sem CMV" na tabela, nunca como zero.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <b>
            Sem giro — {ve.parados.length} SKUs, {fmtInt(somaUnidades(ve.parados))} unidades, não
            valorados.
          </b>{' '}
          O teste de 3× compara o CMV com o preço de saída na nota, e quem não vendeu não tem nota —
          então o custo desses SKUs entraria sem nenhuma conferência. Medido em 15/09/2026, era
          exatamente ali que estava o problema: <b>80% do valor vinha de 12 SKUs com CMV acima de
          R$ 500/un</b>, sete deles errados por cerca de mil vezes (<code>LS0044</code>, copo de
          R$ 8,15 com CMV de R$ 3.386, valia R$ 328 mil sozinho).
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          25 maiores valores em estoque
        </Typography>
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Produto</TableCell>
                <TableCell>Fornecedor</TableCell>
                <TableCell align="right">Estoque</TableCell>
                <TableCell align="right">CMV un.</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="right">Cobertura</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topValor.map((l) => (
                <TableRow key={l.codigo}>
                  <TableCell>
                    <Typography variant="body2">{l.nome}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {l.codigo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {fornecedorDe(l, desc) || <i>não informado</i>}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtInt(l.estoque)}</TableCell>
                  <TableCell align="right">{fmtBrl(l.cmv_unitario)}</TableCell>
                  <TableCell align="right">
                    <b>{fmtBrl(l.valor_estoque)}</b>
                  </TableCell>
                  <TableCell align="right">
                    {l.cobertura_dias == null ? '—' : `${fmtDec1(l.cobertura_dias)}d`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Stack>
  )

  // -------------------------------------------------------------- aba: descontinuados
  const fornecedoresMortos = (payload?.descontinuados || []).filter((d) => d.tipo === 'fornecedor')
  const mortos = linhas.filter((l) => estaDescontinuado(l, desc))

  const abaDescontinuados = () => (
    <Stack spacing={2}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2">
          Produtos que não serão mais comprados. Saem da sugestão, dos KPIs e dos pedidos por
          fornecedor — mas continuam aqui, com o estoque restante, para você acompanhar a queima.
          <b> É decisão sua, guardada nesta tela: não altera o Tiny.</b>
        </Typography>
        {mortos.length > 0 ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>{mortos.length} SKUs</b> · {fmtInt(somaUnidades(mortos))} unidades em estoque ·{' '}
            {fmtBrl(somaValor(mortos.filter((l) => !l.cmv_suspeito)))} a escoar.
          </Typography>
        ) : null}
      </Alert>

      {fornecedoresMortos.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Fornecedores fora de linha
          </Typography>
          <Stack spacing={1}>
            {fornecedoresMortos.map((d) => (
              <Paper
                key={d.id}
                variant="outlined"
                sx={{ p: 1.2, display: 'flex', justifyContent: 'space-between', gap: 2 }}
              >
                <Typography variant="body2">
                  <b>{d.valor}</b>{' '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    · {linhas.filter((l) => l.fornecedor === d.valor).length} SKUs
                  </Box>
                </Typography>
                <Button size="small" disabled={ocupado} onClick={() => void reativar(d.id)}>
                  ↩ Reativar
                </Button>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      {mortos.length > 0 ? (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            {cabecalho(true)}
            <TableBody>
              {[...mortos]
                .sort((a, b) => (b.valor_estoque || 0) - (a.valor_estoque || 0))
                .map((l) => linhaTabela(l, true))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nada descontinuado ainda. Use o botão <b>⊘ Descontinuar</b> na lista de SKUs ou no bloco do
          fornecedor.
        </Typography>
      )}
    </Stack>
  )

  // ------------------------------------------------------------ aba: estoque sem giro
  const parados = payload?.estoque_parado || []
  const abaParado = () => (
    <Stack spacing={1.5}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2">
          SKUs simples com saldo no Tiny e <b>nenhuma venda</b> nos últimos {payload?.janela.dias}{' '}
          dias. Não entram na sugestão de compra — a lista serve para o lado oposto: queima, lote B2B
          ou desconto.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <b>{fmtInt(parados.length)} SKUs</b> · <b>{fmtInt(somaUnidades(parados))} unidades</b>{' '}
          paradas.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <b>Sem valor em reais, de propósito.</b> Valorar exige conferir o CMV contra o preço pelo
          qual o SKU saiu na nota, e quem não vendeu não tem nota. Quando esse estoque era valorado,{' '}
          <b>80% do total vinha de 12 SKUs com CMV acima de R$ 500 por unidade</b> — sete deles
          errados por cerca de mil vezes. Decisão sua em 15/09/2026: <b>só preencher CMV onde há
          giro</b>.
        </Typography>
      </Alert>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell>Fornecedor</TableCell>
              <TableCell align="right">Estoque</TableCell>
              <TableCell align="right">Custo no Tiny</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parados.slice(0, limite).map((p) => (
              <TableRow key={p.codigo}>
                <TableCell>
                  <Typography variant="body2">{p.nome || '—'}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {p.codigo}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {p.fornecedor || <i>não informado</i>}
                  </Typography>
                </TableCell>
                <TableCell align="right">{fmtInt(p.estoque)}</TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    {p.custo_unitario != null ? fmtBrl(p.custo_unitario) : <i>não informado</i>}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {parados.length > limite ? (
        <Button onClick={() => setLimite((n) => n + PAGINA)}>
          Mostrar mais {Math.min(PAGINA, parados.length - limite)} (de{' '}
          {fmtInt(parados.length - limite)} restantes)
        </Button>
      ) : null}
    </Stack>
  )

  // --------------------------------------------------------------- aba: fora do cálculo
  const abaFora = () => (
    <Stack spacing={1.5}>
      <Alert severity="info" icon={false}>
        <Typography variant="body2">
          Estes SKUs <b>venderam</b> no período mas não têm cadastro de SKU simples no Tiny, então
          não há estoque para comparar. Não é que estejam zerados — é que não dá para saber.
        </Typography>
      </Alert>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU na nota</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Unidades</TableCell>
              <TableCell align="right">Faturado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(payload?.sem_cadastro || []).map((s) => (
              <TableRow key={s.codigo_nf}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{s.codigo_nf}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {s.desc || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{fmtInt(s.unid)}</TableCell>
                <TableCell align="right">{fmtBrl(s.valor)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  )

  // ----------------------------------------------------------------- aba: metodologia
  const r = payload?.regras
  const abaMetodo = () => (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Como cada número é calculado
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            <b>Giro diário</b> = unidades vendidas ÷ {payload?.janela.dias} dias. As unidades vêm das{' '}
            <b>notas fiscais</b>, item a item, onde o kit já aparece decomposto no componente que sai
            do estoque — o único lugar da base onde SKU e quantidade estão no mesmo nível do estoque.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>SKU novo:</b> quem nunca teve nota antes de {fmtDia(payload?.janela.de)} e estreou
            depois dos primeiros {r?.folga_estreia} dias tem o giro contado desde a estreia. São{' '}
            {payload?.meta.novos}, marcados na tabela.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Estoque mínimo</b> = {r?.dias_minimo} dias de giro · <b>máximo</b> = {r?.dias_maximo}{' '}
            dias · <b>ponto de reposição</b> = giro × lead time + mínimo.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Lead time</b> pela origem fiscal: nacional {r?.lead_nacional} dias, importado{' '}
            {r?.lead_importado} dias. Sem origem cadastrada usa o nacional (mais conservador) e marca
            "estimado".
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Sugestão</b> = estoque máximo − estoque atual, só quando o estoque caiu ao ponto de
            reposição.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Valor em estoque</b> = saldo × <b>CMV</b>, de <code>ecommerce_financial.cmvs</code>{' '}
            (registro mais recente de cada SKU). O <code>preco_custo</code> do Tiny <b>não</b> é
            usado. SKU sem CMV aparece como "sem CMV", nunca como zero.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Só o que tem giro é valorado</b> — decisão sua de 15/09/2026, depois de medirmos que
            80% daquele valor vinha de 12 SKUs com CMV acima de R$ 500/un, sete deles errados por
            cerca de mil vezes.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Quanto vai custar o pedido não é calculado aqui.</b> A coluna <b>Comprar</b> diz a
            quantidade; o único dinheiro que este painel mostra é o que já está parado no estoque.
          </Typography>
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          O que este painel não sabe
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            🔴 <b>O que já está comprado.</b> Nenhuma ordem de compra do Tiny diz se foi recebida —
            todas em <code>situacao: "0"</code> desde 2024, sem nota vinculada nem data prevista
            {payload?.meta.ordens
              ? ` (medido em ${fmtDia(payload.meta.ordens.medido_em)}: ${fmtInt(
                  payload.meta.ordens.total,
                )} ordens)`
              : ''}
            . A sugestão não desconta nada. <b>Confira no Tiny antes de fechar o pedido.</b>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⚠️ <b>{payload?.meta.negativos} SKUs com saldo negativo</b> no Tiny entram como estoque
            zero e ficam marcados — saldo negativo não é estoque, é erro de movimentação.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⚠️ <b>Cadastro incompleto.</b> SKUs sem saldo e sem custo aparecem marcados e todos caem
            em "comprar agora", porque estoque zero é estoque zero. Pode ser ruptura real ou cadastro
            pela metade — o painel não adivinha qual. Há um caso conhecido de classificação trocada
            no Tiny: na família <code>SHIMMERSH12</code>, os itens "12pçs" estão como SKU simples e
            os "1pç" como kit de 12.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⚠️ <b>Estoque de marketplace não é confiável no Tiny</b> (Full do ML, Full Shopee, FBA).
            O saldo aqui é o físico dos depósitos próprios.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⚠️ <b>Sem ajuste de sazonalidade.</b> O giro é média simples do período — em artigo de
            festa isso pesa, e a leitura de setembro não vale para dezembro.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⚠️ <b>O corte de "custo em revisão" é sensível na fronteira.</b> Um SKU cujo CMV está
            perto de 3× o preço médio de saída pode entrar ou sair do bloco conforme as vendas da
            janela mudam esse preço médio. Não é erro: é a regra funcionando.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Fornecedor: três origens, sempre ditas.</b> <i>Do cadastro</i> — veio do campo
            fornecedor no Tiny. <i>Confirmado por você</i> — SKU ativo sem fornecedor no cadastro,
            atribuído à <b>{FORNECEDOR_CONFIRMADO}</b> pela regra que você confirmou em 11/09/2026;
            aparece marcado em cada linha e não vem do ERP. <i>Não informado</i> — SKU descontinuado
            sem fornecedor, onde a regra não vale.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Somente leitura nos dados da empresa.</b> Nada é escrito no Tiny. As únicas gravações
            são as suas decisões nesta tela — descontinuados e o pedido em montagem — em duas
            collections exclusivas do painel.
          </Typography>
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          De onde vem cada número
        </Typography>
        <Stack spacing={0.5}>
          {Object.entries(payload?.fontes || {}).map(([k, v]) => (
            <Typography key={k} variant="caption" color="text.secondary">
              <b>{k}:</b> {v}
            </Typography>
          ))}
          {Object.entries(payload?.resolucao || {}).map(([k, v]) => (
            <Typography key={k} variant="caption" color="text.secondary">
              <b>resolução {k}:</b> {fmtInt(v.codigos)} códigos de nota, {fmtInt(v.unidades)}{' '}
              unidades
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  )

  const abas: Array<[Aba, string, number]> = [
    ['lista', 'Todos os SKUs', 0],
    ['forn', 'Por fornecedor', 0],
    ['pedido', 'Pedido', pedido.length],
    ['imp', 'Simular importação', 0],
    ['valor', 'Valor do estoque', 0],
    ['desc', 'Descontinuados', nDesc],
    ['parado', 'Estoque sem giro', 0],
    ['fora', 'Fora do cálculo', 0],
    ['metodo', 'Metodologia', 0],
  ]

  return (
    <DomainPageShell
      title="Recompra"
      subtitle={subtitulo}
      loading={carregando}
      error={erro}
      onRetry={() => void carregar()}
      stats={stats}
    >
      <Stack spacing={2}>
        {ocupado ? <LinearProgress /> : null}

        <Alert severity="warning" icon={false}>
          <Typography variant="body2">
            <b>O que já foi pedido não está descontado.</b> A API do Tiny não distingue ordem de
            compra em aberto de já recebida — <code>situacao</code> vem "0" em 100% das ordens desde
            2024, sem nota fiscal vinculada nem data prevista. Confira no Tiny antes de comprar: o
            painel pode sugerir algo que já está a caminho.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>
              {vivos.filter(cadastroIncompleto).length} SKUs estão sem saldo e sem preço de custo no
              Tiny ao mesmo tempo
            </b>{' '}
            e todos caem em "comprar agora". Pode ser ruptura de verdade ou cadastro pela metade — o
            painel não tem como distinguir, então marca e não decide. Filtre por{' '}
            <b>⚠ Cadastro incompleto</b> para revisá-los antes de comprar.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <b>{confirmados} SKUs não têm fornecedor no cadastro do Tiny</b> e estão atribuídos à{' '}
            <b>{FORNECEDOR_CONFIRMADO}</b> — regra que <b>você confirmou</b> em 11/09/2026. Cada
            linha mostra "confirmado por você", para não se confundir com o que vem do ERP.
          </Typography>
        </Alert>

        {msg ? (
          <Alert severity="info" onClose={() => setMsg(null)}>
            {msg}
          </Alert>
        ) : null}

        {payload?.status?.ultimo_erro ? (
          <Alert severity="error">
            O último recálculo falhou: {payload.status.ultimo_erro}
          </Alert>
        ) : null}
        {!emAndamento && (statusVivo?.ultimo_erro_tiny || payload?.estoque_tiny?.erro) ? (
          <Alert severity="warning">
            Na última atualização o Tiny não respondeu por completo — a base saiu com o saldo
            que havia: {statusVivo?.ultimo_erro_tiny || payload?.estoque_tiny?.erro}
          </Alert>
        ) : null}
        {!emAndamento && (statusVivo?.aviso_tiny || payload?.estoque_tiny?.aviso) ? (
          <Alert severity="info">
            Na última atualização parte do saldo veio pela API v2 do Tiny (reserva), porque a v3
            não estava disponível: {statusVivo?.aviso_tiny || payload?.estoque_tiny?.aviso}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip title="Consulta o saldo de agora no Tiny (API v3) para todos os SKUs com giro (os que recebem sugestão) e recalcula. Leva uns 20 minutos — o Tiny limita a 120 consultas por minuto; a tela recarrega sozinha.">
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={ocupado || emAndamento}
                onClick={() => void atualizarBase('rapido')}
              >
                {emAndamento ? 'Atualizando…' : '↻ Atualizar agora'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Consulta TODOS os SKUs simples das duas contas do Tiny (~65 min). Use quando quiser o estoque parado e o valor de estoque também no dia.">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={ocupado || emAndamento}
                onClick={() => void atualizarBase('completo')}
              >
                Completa
              </Button>
            </span>
          </Tooltip>
          {emAndamento ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 260 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {textoAndamento}
              </Typography>
            </Stack>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {rotuloEstoque} · Base de{' '}
            {fmtDataHora(payload?.status?.ultima_atualizacao || payload?.gerado_em)}
          </Typography>
          {idadeDias != null && idadeDias >= 1 && !emAndamento ? (
            <Chip
              size="small"
              color={idadeDias >= 3 ? 'error' : 'warning'}
              label={`saldo do Tiny com ${idadeDias} dia${idadeDias > 1 ? 's' : ''} — clique em Atualizar agora`}
            />
          ) : null}
        </Stack>
        {emAndamento && statusVivo?.etapa === 'tiny' && statusVivo.tiny?.total ? (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (100 * statusVivo.tiny.feitos) / statusVivo.tiny.total)}
          />
        ) : emAndamento ? (
          <LinearProgress />
        ) : null}

        <Tabs
          value={aba}
          onChange={(_, v: Aba) => {
            setAba(v)
            setLimite(PAGINA)
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {abas.map(([k, rotulo, n]) => (
            <Tab key={k} value={k} label={n ? `${rotulo} (${n})` : rotulo} />
          ))}
        </Tabs>

        {aba === 'lista' ? abaLista() : null}
        {aba === 'forn' ? abaFornecedor() : null}
        {aba === 'pedido' ? abaPedido() : null}
        {aba === 'imp' ? abaImportacao() : null}
        {aba === 'valor' ? abaValor() : null}
        {aba === 'desc' ? abaDescontinuados() : null}
        {aba === 'parado' ? abaParado() : null}
        {aba === 'fora' ? abaFora() : null}
        {aba === 'metodo' ? abaMetodo() : null}

        {payload ? (
          <PropostaIaCard
            title="Leitura da Recompra"
            caption="Verdade = KPIs e tabelas (sugestão, pedido, importação). Proposta = leitura da IA — revise antes de comprar."
            state={leituraIa}
            onRetry={() => void carregarLeitura()}
          />
        ) : null}
      </Stack>
    </DomainPageShell>
  )
}


/**
 * Barras semanais desde a estreia. Depois do esgotamento as barras ficam apagadas e
 * uma linha tracejada marca a semana. É uma série só — a legenda é a coluna.
 */
const Sparkline = memo(function Sparkline({
  semanas,
  esgotouNaSemana,
  esgotouEm,
}: {
  semanas: number[]
  esgotouNaSemana: number | null
  esgotouEm: string | null
}) {
  const tema = useTheme()
  if (!semanas.length) return null
  const W = 96
  const H = 24
  const gap = 1
  const bw = Math.max(1, (W - gap * (semanas.length - 1)) / semanas.length)
  const mx = Math.max(1, ...semanas)
  const xEsg = esgotouNaSemana == null ? null : (esgotouNaSemana + 1) * (bw + gap) - gap / 2
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="vendas por semana">
      {semanas.map((v, i) => {
        const h = Math.max(v > 0 ? 1.5 : 0, (v / mx) * H)
        const depois = esgotouNaSemana != null && i > esgotouNaSemana
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={H - h}
            width={bw}
            height={h}
            rx={1}
            fill={depois ? tema.palette.divider : tema.palette.success.main}
          >
            <title>{`semana ${i + 1}: ${v} un`}</title>
          </rect>
        )
      })}
      {xEsg != null ? (
        <line
          x1={xEsg}
          y1={0}
          x2={xEsg}
          y2={H}
          stroke={tema.palette.error.main}
          strokeWidth={1.5}
          strokeDasharray="2 2"
        >
          <title>{`esgotou em ${fmtDia(esgotouEm)}`}</title>
        </line>
      ) : null}
    </svg>
  )
})

/**
 * Uma linha da tabela, memoizada.
 *
 * Sem o `memo`, mexer em qualquer filtro remonta todas as linhas visíveis — e cada uma
 * traz chip, botão, checkbox e meia dúzia de rótulos. Com 200 na tela a aba ficava sem
 * resposta por dezenas de segundos (medido em 18/09/2026).
 */
const LinhaSku = memo(function LinhaSku({
  l,
  comForn,
  morto,
  porFornecedor,
  soProduto,
  dentro,
  forn,
  confirmado,
  ocupado,
  onToggle,
  onDescontinuar,
  onReativar,
}: {
  l: LinhaRecompra
  comForn: boolean
  morto: boolean
  porFornecedor: boolean
  soProduto: boolean
  dentro: boolean
  forn: string | null
  confirmado: boolean
  ocupado: boolean
  onToggle: (l: LinhaRecompra) => void
  onDescontinuar: (tipo: 'produto' | 'fornecedor', valor: string, nome?: string) => void
  onReativar: (id: string) => void
}) {
  return (
    <TableRow
        key={l.codigo}
        hover
        sx={{
          opacity: morto ? 0.6 : 1,
          bgcolor: dentro ? 'action.selected' : undefined,
          // 13 colunas não cabem em tela de notebook: em vez de espremer cada uma até
          // o nome do fornecedor virar cinco linhas, a tabela rola na horizontal —
          // mesmo comportamento do painel original.
          '& td': { whiteSpace: 'nowrap', verticalAlign: 'top' },
        }}
      >
        <TableCell padding="checkbox">
          {/* SKU descontinuado não recebe tique: não se compra o que saiu de linha */}
          {morto ? null : (
            <input
              type="checkbox"
              checked={dentro}
              disabled={ocupado}
              onChange={() => onToggle(l)}
              aria-label={`incluir ${l.codigo} no pedido`}
            />
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'normal !important' }}>
          <Typography
            variant="body2"
            sx={{ textDecoration: morto ? 'line-through' : undefined, maxWidth: 320 }}
          >
            {l.nome || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {l.codigo}
          </Typography>
        </TableCell>
        {comForn ? (
          <TableCell sx={{ maxWidth: 210 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              title={forn || undefined}
              sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {forn || <i>não informado</i>}
            </Typography>
            {confirmado ? (
              <Typography variant="caption" color="text.secondary" display="block">
                confirmado por você
              </Typography>
            ) : null}
          </TableCell>
        ) : null}
        <TableCell align="right">
          {morto ? (
            <>
              <Chip size="small" label="Descontinuado" />
              {porFornecedor ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  pelo fornecedor
                </Typography>
              ) : null}
            </>
          ) : (
            <Chip size="small" color={COR_URGENCIA[l.urgencia]} label={ROTULO_URGENCIA[l.urgencia]} />
          )}
        </TableCell>
        <TableCell align="right">
          {fmtInt(l.estoque)}
          {l.estoque_negativo ? (
            <Typography variant="caption" color="warning.main" display="block">
              saldo {fmtInt(l.estoque_bruto)} no Tiny
            </Typography>
          ) : cadastroIncompleto(l) ? (
            <Typography variant="caption" color="warning.main" display="block">
              sem saldo e sem custo no Tiny
            </Typography>
          ) : null}
        </TableCell>
        <TableCell align="right">
          {l.estoque === 0 ? (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          ) : l.valor_estoque == null ? (
            <Typography variant="caption" color="warning.main">
              sem CMV
            </Typography>
          ) : (
            <>
              {fmtBrl(l.valor_estoque)}
              <Typography variant="caption" color="text.secondary" display="block">
                {fmtBrl(l.cmv_unitario)}/un
              </Typography>
              {l.cmv_suspeito ? (
                <Typography variant="caption" color="warning.main" display="block">
                  CMV em revisão
                </Typography>
              ) : null}
            </>
          )}
        </TableCell>
        <TableCell align="right">
          {fmtDec1(l.giro_diario)}
          {l.sku_novo ? (
            <Typography variant="caption" color="text.secondary" display="block">
              novo · {l.dias_base}d
            </Typography>
          ) : null}
        </TableCell>
        <TableCell align="right">
          {l.cobertura_dias == null ? '—' : `${fmtDec1(l.cobertura_dias)}d`}
        </TableCell>
        <TableCell align="right">{fmtInt(l.ponto_reposicao)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {morto ? '—' : l.quantidade_sugerida ? fmtInt(l.quantidade_sugerida) : '—'}
        </TableCell>
        <TableCell align="right">{fmtInt(l.unidades_periodo)}</TableCell>
        <TableCell align="right">
          {l.lead_time_dias}d
          {l.lead_time_estimado ? (
            <Typography variant="caption" color="text.secondary" display="block">
              estimado
            </Typography>
          ) : null}
        </TableCell>
        <TableCell align="right">
          {soProduto ? (
            <Button size="small" disabled={ocupado} onClick={() => onReativar(idProduto(l.codigo))}>
              ↩ Reativar
            </Button>
          ) : porFornecedor ? (
            <Typography variant="caption" color="text.secondary">
              via fornecedor
            </Typography>
          ) : (
            <Button
              size="small"
              color="inherit"
              disabled={ocupado}
              onClick={() => onDescontinuar('produto', l.codigo, l.nome || l.codigo)}
            >
              ⊘ Descontinuar
            </Button>
          )}
        </TableCell>
      </TableRow>
    )
})
